import React from "react";
import { UpdateHtReading } from "../../../services/infrastructure/HtService";
import { IHTReading } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import {
  IStackTokens,
  Stack,
  DefaultButton,
  PrimaryButton,
  Label,
  TextField,
  PanelType,
  values,
  Toggle,
} from "office-ui-fabric-react";
import { getAlertDetails } from "../../../services/ThresholdService";
import CustomDrawer from "../../common/CustomDrawer";
import { Formik } from "formik";
import * as yup from "yup";
import { CustomTextField } from "../../common/Library";
import { CategoryHeader } from "../Misc";

const demandAndPfSchema = yup.object().shape({
  press: yup.number().required("required"),
  cumu: yup.number().required("required"),
});

const currentSchema = yup.object().shape({
  R: yup.number().required("required"),
  Y: yup.number().required("required"),
  B: yup.number().required("required"),
});
const voltageSchema = yup.object().shape({
  RY: yup.number().required("required"),
  YB: yup.number().required("required"),
  BR: yup.number().required("required"),
});

const htReadingSchema = yup.object().shape({
  htVoltage: voltageSchema,
  htAmps: currentSchema,
  frequency: yup.number().required("required"),
  kwh: yup.number().required("required"),
  kvah: yup.number().required("required"),
  demand: demandAndPfSchema,
  pf: demandAndPfSchema,
  recordedBy: yup.string().required("required"),
  remarks: yup.string().optional(),
});

export interface ManageHtPanelDialogProps {
  data?: IHTReading;
  handleLoading: any;
  handleDialogClose: any;
  threshold: any;
}

const ManageHtPanelDialog: React.FC<ManageHtPanelDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  threshold,
}) => {
  //state
  const [state, setState] = React.useState({
    htReading: {
      ...data,
    },
    isLoading: false,
  } as {
    htReading: IHTReading;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const wrapStackTokens: IStackTokens = { childrenGap: 8 };

  const closeUpdateDialoge = (data?: IHTReading) => {
    handleDialogClose(data);
  };

  const onBlurFunc = (value: any, path: string) => {
    console.log("onBlurFunc");
    const { showAlert, msg } = getAlertDetails(path, threshold, value);
    console.log("[showAlert]", showAlert, msg);
    if (showAlert === true) {
      showConfirmDialog("Alert", msg as string, () => { }, "Ok", true);
    }
  };

  return (
    <React.Fragment>
      <CustomDrawer
        drawerType={PanelType.medium}
        title={"Update Ht-Panel Reading"}
        handelOpen={true}
        onClose={() => closeUpdateDialoge()}
      >
        <Stack>
          <Formik
            initialValues={state.htReading}
            // validationSchema={htReadingSchema}
            onSubmit={async (values: IHTReading, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              result = await UpdateHtReading(obj);
              handleLoading();
              if (result?.success) {
                closeUpdateDialoge(result.data);
                showToast(`Ht-Panel Reading updated successfully`, "success");
              } else {
                showToast(`Error while updating an ht-panel reading`, "error");
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-toggle">
                    <Toggle
                      checked={values.isSystemOn}
                      label="System"
                      onText="On"
                      offText="Off"
                      onChange={(e, v) => {
                        setFieldValue("isSystemOn", v);
                      }}
                    />
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="HT Voltage" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htVoltage.RY"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"RY"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htVoltage.YB"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"YB"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htVoltage.BR"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"BR"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="HT Current" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htAmps.R"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"R"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htAmps.Y"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Y"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"htAmps.B"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"B"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    {/* <CategoryHeader title="Frequency (Hz)" /> */}
                    <CustomTextField
                      name={"frequency"}
                      onBlurFunc={onBlurFunc}
                      type="number"
                      label={"frequency"}
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="Demand" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"demand.press"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"PRES"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"demand.cumu"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CUMU"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="PF" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"pf.press"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"PRES"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"pf.cumu"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"CUMU"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="Others" />
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      className="pt-24"
                    >
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kwh"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"KWH"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kvah"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"KVAH"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="General" />
                    <Stack tokens={wrapStackTokens} className="pt-24">
                      <Stack.Item>
                        <CustomTextField
                          name={"remarks"}
                          label={"Remarks (If Any)"}
                          multiline
                          autoAdjustHeight
                        />
                      </Stack.Item>
                      <Stack.Item className="pt-24">
                        <CustomTextField
                          name={"recordedBy"}
                          label={"Shift Tech Sign"}
                          disabled={true}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <br />
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeUpdateDialoge()}
                        className="borderless-btn"
                      />
                      <PrimaryButton type="submit" text="Update" />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageHtPanelDialog;
